import React, {
  useState,
  useEffect,
  useReducer,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronUp } from "lucide-react";
import { t, textDir, trServer, useLang } from "../../lib/i18n";
import { dealerAvatar } from "../../lib/brand";

let _uid = 0;

// Runs on the raw server log, which is always French (see lib/i18n/server.js).
function classify(text) {
  if (!text) return "info";
  if (/s'est (re)?connecté|s'est déconnecté/.test(text)) return "presence";
  if (/⏱/.test(text)) return "event"; // the turn timer ran out: the Dealer played for somebody
  if (/erreur|error/i.test(text) && !/récupéré|réussie/i.test(text)) return "error";
  if (/⚠️|pénalité|pénalit|sanction|annulé.*pts/i.test(text)) return "penalty";
  if (/Nefrech|gagné la manche|🏆|remporte|[Éé]liminé|Brelan de .* complet/i.test(text)) return "event";
  if (/Nemna3|posé|posée|joker.*inséré|inséré|récupéré|ajoutée?|carte.*prise|annul|↩️/i.test(text)) return "success";
  if (/défaussé|manche rejoué|distribué|pioch/i.test(text)) return "action";
  return "info";
}

const TYPE = {
  error:   { ring: "ring-red-500",       shadow: "shadow-red-500/40",     bubble: "bg-red-950/92 border-red-500/50 text-red-100",        dot: "bg-red-400",     ping: true  },
  penalty: { ring: "ring-rose-500",       shadow: "shadow-rose-500/30",    bubble: "bg-rose-950/88 border-rose-500/40 text-rose-100",     dot: "bg-rose-400",    ping: true  },
  event:   { ring: "ring-amber-400",      shadow: "shadow-amber-500/30",   bubble: "bg-amber-950/88 border-amber-400/40 text-amber-100",  dot: "bg-amber-300",   ping: true  },
  success: { ring: "ring-emerald-400/70", shadow: "shadow-emerald-500/20", bubble: "bg-emerald-950/85 border-emerald-400/30 text-emerald-100", dot: "bg-emerald-400", ping: false },
  info:    { ring: "ring-sky-400/50",     shadow: "shadow-sky-500/10",     bubble: "bg-slate-900/88 border-slate-600/30 text-slate-200",  dot: "bg-sky-400",     ping: false },
  action:  { ring: "ring-slate-500/30",   shadow: "shadow-black/10",       bubble: "bg-slate-900/72 border-slate-700/30 text-slate-400",  dot: "bg-slate-500",   ping: false },
  // Connections / disconnections: journal only, see ingest().
  presence: { ring: "ring-slate-500/30",  shadow: "shadow-black/10",       bubble: "bg-slate-900/72 border-slate-700/30 text-slate-400",  dot: "bg-slate-500",   ping: false },
};

// Persistent turn-hint styles per phase
const HINT_STYLE = {
  drawing: {
    bubble: "bg-amber-950/70 border-amber-400/35 text-amber-100/90",
    ring:   "ring-amber-400/50",
    shadow: "shadow-amber-500/20",
  },
  acting_pending: {
    bubble: "bg-emerald-950/70 border-emerald-400/35 text-emerald-100/90",
    ring:   "ring-emerald-400/50",
    shadow: "shadow-emerald-500/20",
  },
  acting: {
    bubble: "bg-slate-900/70 border-white/12 text-white/60",
    ring:   "ring-white/15",
    shadow: "shadow-black/10",
  },
};

const PEEK_LIFETIME = 8000;
const AVATAR_H      = 36;
const GAP           = 8;

export const GameFeed = forwardRef(function GameFeed({ actionLog, roundNumber, turnHint }, ref) {
  const [messages,  setMessages]  = useState([]);
  const [peek,      setPeek]      = useState(null);
  const [hasUnread, setHasUnread] = useState(false);
  const [open,      setOpen]      = useState(false);
  const [, forceUpdate] = useReducer(n => n + 1, 0);
  const { dir, lang } = useLang();

  const openRef    = useRef(false);
  const prevLen    = useRef(0);
  const prevRound  = useRef(null);
  const bottomRef  = useRef(null);
  const lastType   = useRef("info");
  const containerRef = useRef(null);

  openRef.current = open;

  const getLiveAnchor = () => {
    if (!containerRef.current) return { left: 8, bottom: 140 };
    const r = containerRef.current.getBoundingClientRect();
    return { left: r.left, bottom: window.innerHeight - r.bottom };
  };

  const scrollBottom = () =>
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 80);

  function showPeek(msg) {
    setPeek({ ...msg, peekId: ++_uid });
  }

  // `transient`: shown in the bubble only, never kept in the journal (the Dealer's nudges to a player
  // who lets the turn sit: owner's request, October 2026).
  function ingest(text, type, { transient = false } = {}) {
    const resolved = type || classify(text);
    const msg = { id: ++_uid, text, type: resolved, ts: Date.now() };
    if (transient) {
      if (!openRef.current) showPeek(msg);
      return;
    }
    setMessages((prev) => [...prev, msg].slice(-50));
    // A flaky connection produces these in bursts: they are kept in the
    // journal but never take the bubble away from the game's own messages.
    if (resolved === "presence") {
      if (openRef.current) scrollBottom();
      return;
    }
    lastType.current = resolved;
    if (!openRef.current) {
      showPeek(msg);
      setHasUnread(true);
    } else {
      scrollBottom();
    }
  }

  useImperativeHandle(ref, () => ({ push: ingest }), []); // eslint-disable-line react-hooks/exhaustive-deps

  // The open journal closes on a tap anywhere else (the avatar's own tap toggles it). That tap does what it
  // would do anyway: on the table it also empties the hand's selection (Game.jsx).
  const panelRef = useRef(null);
  const avatarRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target) || avatarRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [open]);

  useEffect(() => {
    window.addEventListener("resize", forceUpdate);
    return () => window.removeEventListener("resize", forceUpdate);
  }, [forceUpdate]);

  useEffect(() => {
    if (prevRound.current !== null && roundNumber !== prevRound.current) {
      setMessages([]);
      setPeek(null);
      setHasUnread(false);
      prevLen.current = 0;
    }
    prevRound.current = roundNumber;
  }, [roundNumber]);

  useEffect(() => {
    const log = actionLog || [];
    if (prevLen.current === 0) { prevLen.current = log.length; return; }
    if (log.length < prevLen.current) { prevLen.current = log.length; return; }
    const newRaw = log.slice(prevLen.current);
    prevLen.current = log.length;
    newRaw.filter(Boolean).forEach((text) => ingest(text));
  }, [actionLog]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) {
      setHasUnread(false);
      setPeek(null);
      scrollBottom();
    }
  }, [open]);

  // A message stays PEEK_LIFETIME in the bubble, the next one starts the count
  // again. Tied to the message shown: a timer kept in a ref and cleared on
  // unmount was lost to React's remount in development (StrictMode), and the
  // first message stayed for good, hiding the turn hint.
  useEffect(() => {
    if (!peek) return undefined;
    const timer = setTimeout(() => setPeek(null), PEEK_LIFETIME);
    return () => clearTimeout(timer);
  }, [peek]);

  const lt          = lastType.current;
  const hasPriority = hasUnread && !open && TYPE[lt]?.ping;

  const a           = getLiveAnchor();
  const peekLeft    = a.left + AVATAR_H + GAP;
  // Bubble slightly above avatar center
  const peekBottom  = a.bottom + AVATAR_H - 22;
  const panelLeft   = a.left + AVATAR_H + GAP;
  const panelBottom = a.bottom;

  // Messages keep their original text and are translated at render time, so
  // the journal follows a language switch.
  const peekText = peek ? trServer(peek.text, peek.id) : "";

  // Hint shows when: it's the player's turn, panel closed, no transient peek
  const showHint = !!turnHint && !open && !peek;
  const hintStyle = turnHint ? HINT_STYLE[turnHint.type] : null;

  // Avatar ring reflects hint type when hint is active (and no unread override)
  const avatarRing = open
    ? "ring-rose-400/60 shadow-black/20"
    : hasUnread
    ? `${TYPE[lt].ring} ${TYPE[lt].shadow}`
    : showHint
    ? `${hintStyle.ring} ${hintStyle.shadow}`
    : "ring-white/15 shadow-black/10";

  return (
    <div className="inline-block relative select-none" ref={containerRef}>

      {/* ── Bubble zone: peek (transient) OR turn hint (persistent) ── */}
      <div
        className="fixed z-50 pointer-events-none"
        style={{ left: peekLeft, bottom: peekBottom }}
      >
        <AnimatePresence mode="wait">
          {!open && peek ? (
            /* Transient message peek */
            <motion.button
              key={`peek-${peek.peekId}`}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1    }}
              exit={{    opacity: 0, scale: 0.88 }}
              transition={{ type: "spring", stiffness: 380, damping: 28 }}
              onClick={() => setOpen(true)}
              dir={textDir(peekText)}
              className={[
                "pointer-events-auto max-w-[200px] sm:max-w-[220px] text-start",
                "px-3 py-1.5 rounded-2xl rounded-bl-sm border",
                "text-[10px] sm:text-[11px] leading-snug shadow-lg cursor-pointer backdrop-blur-md",
                TYPE[peek.type].bubble.replace(/(bg-\S+)\/\d+/, "$1/45"),
              ].join(" ")}
            >
              {peekText}
            </motion.button>
          ) : showHint ? (
            /* Persistent turn hint — stays until action or new message */
            <motion.div
              key={`hint-${turnHint.type}`}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1    }}
              exit={{    opacity: 0, scale: 0.88 }}
              transition={{ type: "spring", stiffness: 320, damping: 30 }}
              dir={textDir(turnHint.text)}
              className={[
                "max-w-[200px] sm:max-w-[220px]",
                "px-3 py-1.5 rounded-2xl rounded-bl-sm border",
                "text-[9px] sm:text-[10px] leading-snug shadow-lg backdrop-blur-md",
                "font-semibold tracking-wide uppercase",
                hintStyle.bubble,
              ].join(" ")}
            >
              {turnHint.text}
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>

      {/* ── Avatar button ── */}
      <motion.button
        ref={avatarRef}
        onClick={() => setOpen((v) => !v)}
        whileTap={{ scale: 0.88 }}
        className="relative"
        aria-label={t("feed.aria")}
      >
        {hasPriority && (
          <span className="absolute inset-0 rounded-full animate-ping bg-rose-500/30 pointer-events-none" />
        )}
        <div className={`relative h-9 w-9 rounded-full ring-2 transition-all duration-300 shadow-lg ${avatarRing}`}>
          <img
            src={dealerAvatar(lang)}
            alt={t("feed.dealer")}
            className="h-9 w-9 rounded-full object-cover"
            draggable={false}
          />
          <AnimatePresence>
            {hasUnread && !open && (
              <motion.span
                key="dot"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                transition={{ type: "spring", stiffness: 500, damping: 22 }}
                className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-rose-500 ring-2 ring-slate-950"
              />
            )}
          </AnimatePresence>
        </div>
      </motion.button>

      {/* ── Journal panel — fixed, opens upward to the right ── */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            ref={panelRef}
            // Reading the journal keeps the hand's selection (Game.jsx).
            data-keep-selection
            initial={{ opacity: 0, scaleY: 0.88, y: 6 }}
            animate={{ opacity: 1, scaleY: 1,    y: 0 }}
            exit={{    opacity: 0, scaleY: 0.88, y: 6 }}
            transition={{ type: "spring", stiffness: 420, damping: 32 }}
            style={{ transformOrigin: "bottom left", left: panelLeft, bottom: panelBottom }}
            className="fixed z-50 w-56 sm:w-64 bg-slate-950/94 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl shadow-black/60 overflow-hidden select-none"
          >
            <div dir={dir} className="flex items-center gap-2 px-3 py-2 border-b border-white/8 bg-white/[0.03]">
              <img src={dealerAvatar(lang)} alt="" className="h-5 w-5 rounded-full ring-1 ring-rose-500/50 flex-shrink-0" draggable={false} />
              <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400 flex-1">
                {t("feed.title")}
              </span>
              <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-slate-300 transition-colors p-0.5 rounded">
                <ChevronUp className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="max-h-36 overflow-y-auto px-2.5 py-2 flex flex-col gap-1.5 overscroll-contain">
              {messages.length === 0 ? (
                <p className="text-slate-600 text-[10px] text-center py-4 italic">{t("feed.empty")}</p>
              ) : (
                <AnimatePresence initial={false}>
                  {messages.slice(-16).map((msg) => {
                    const text = trServer(msg.text, msg.id);
                    return (
                      <motion.div
                        key={msg.id}
                        initial={{ opacity: 0, x: -14 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.15 }}
                        dir={textDir(text)}
                        className="flex items-start gap-1.5"
                      >
                        <div className={`mt-[5px] h-1.5 w-1.5 rounded-full flex-shrink-0 ${TYPE[msg.type].dot}`} />
                        <div className={`flex-1 text-[10px] sm:text-[11px] leading-relaxed px-2 py-1 rounded-xl rounded-se-sm border ${TYPE[msg.type].bubble}`}>
                          {text}
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              )}
              <div ref={bottomRef} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

export default GameFeed;
